//@flow
import * as Immutable from 'immutable';
import _ from 'lodash';
import { LatLng } from 'common/models/internal/LatLng';
import { GeocodeEditLocation } from 'common/models/internal/GeocodeEditLocation';
import { GroupedGeocodeEditLocation } from 'common/models/internal/GroupedGeocodeEditLocation';
import { Geometry, ProviderEnumType } from 'common/models/internal/Geometry';
import { LocationWithMetadata } from 'common/models/internal/LocationWithMetadata';
import {
    GeocodeVerifiedGeometry,
    AccuracyCategoryType
} from 'common/models/internal/GeocodeVerifiedGeometry';
import LatLngUtils from 'common/utils/LatLngUtils';
import { memoized } from 'common/utils/Memoizer';
import { getMarkerColorFromConfidence } from 'common/components/core/util/MarkerColorUtil';
import { FilterElementsEnum } from 'common/components/core/filter/Filter';
import { UIFilter } from 'common/models/internal/UIFilter';
import I18nHelper from 'common/helpers/I18nHelper';
const nsTranslate = I18nHelper.getTranslate(['translations']);

type GeocodeEditListType = Immutable.List<GeocodeEditLocation>;
const LocationConfidenceLevels = {
    Low: 'LOW',
    Medium: 'MEDIUM',
    High: 'HIGH'
};

class GeocodeEditUtils {
    static getGeocodeEditsBody({
        updatedLocations,
        updateVerification = false
    }: {
        updatedLocations: Immutable.List<GeocodeEditLocation>,
        updateVerification?: boolean
    }) {
        const edits = [];
        updatedLocations.forEach(editedLocation => {
            const { id: locationId, geometry, verified } = editedLocation;
            let newLocation = {};
            if (updateVerification) {
                newLocation = {
                    locationId,

                    verified
                };
            } else if (geometry && geometry.latLng) {
                const { lat, lng } = geometry.latLng;
                newLocation = {
                    ...newLocation,
                    locationId,
                    latLng: {
                        lat,
                        lng,
                        accuracy: 0
                    }
                };
            }
            edits.push(newLocation);
        });

        return { edits };
    }

    static updateLocationIsOriginal({ editedLocation, updatedIsOriginal }) {
        return editedLocation.set('isOriginal', updatedIsOriginal);
    }

    static updateLocationProvider({ editedLocation, updatedProvider }) {
        return updatedProvider
            ? editedLocation.setIn(['geometry', 'provider'], updatedProvider)
            : editedLocation;
    }

    static updateLocationConfidence({
        editedLocation,
        updatedConfidence = LocationConfidenceLevels.High
    }) {
        return editedLocation.setIn(
            ['geometry', 'confidence'],
            updatedConfidence
        );
    }

    static updateLocationOriginalLatLng({
        editedLocation,
        updatedOriginalLatLng
    }) {
        const newLatLng = LatLng.fromJS({
            lat: updatedOriginalLatLng.lat,
            lng: updatedOriginalLatLng.lng,
            accuracy: updatedOriginalLatLng.accuracy
        });
        return editedLocation.setIn(['originalGeometry', 'latLng'], newLatLng);
    }

    static updateLocationLatLng({ editedLocation, updatedLatLng }) {
        const newLatLng = LatLng.fromJS({
            lat: updatedLatLng.lat,
            lng: updatedLatLng.lng,
            accuracy: updatedLatLng.accuracy
        });
        return editedLocation.setIn(['geometry', 'latLng'], newLatLng);
    }

    static updateMultipleLocationGeometries({ editedLocations, allLocations }) {
        let updatedLocations = Immutable.List();
        let oldLocations = Immutable.List();
        editedLocations.forEach((editedLocation, id) => {
            const oldLocation = allLocations.find(
                location => location.id === id
            );
            const updatedLocation = oldLocation
                .setGeometry(Geometry.fromJS(editedLocation.geometry))
                .setIsOriginal(editedLocation.isOriginal);
            updatedLocations = updatedLocations.push(updatedLocation);
            oldLocations = oldLocations.push(oldLocation);
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedLocations, updatedAllLocations, oldLocations };
    }

    static updateMultipleLocationGeometryFields({
        editedLocations,
        updatedLatLng,
        updatedConfidence,
        updatedProvider = ProviderEnumType.CLIENT_READ,
        allLocations
    }) {
        let updatedLocations = Immutable.List();
        editedLocations.forEach(location => {
            const { geometry, originalGeometry } = location;
            if (
                !geometry ||
                !geometry.latLng ||
                (geometry &&
                    geometry.latLng &&
                    geometry.latLng.isDifferent(updatedLatLng))
            ) {
                const updatedLocation = GeocodeEditUtils.updateLocationGeometryFields(
                    {
                        editedLocation: location,
                        updatedLatLng,
                        updatedConfidence,
                        updatedProvider,
                        updatedIsOriginal: geometry.latLng ? false : true,
                        updatedOriginalLatLng: originalGeometry.latLng
                            ? undefined
                            : updatedLatLng
                    }
                );
                updatedLocations = updatedLocations.push(updatedLocation);
            }
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedAllLocations, updatedLocations };
    }

    static resetMultipleLocationsToOld({ allLocations, oldLocationEntities }) {
        let updatedLocations = Immutable.List();
        oldLocationEntities.forEach(oldLocationEntity => {
            const updatedLocation = GeocodeEditUtils.updateLocationGeometryFields(
                {
                    editedLocation: oldLocationEntity,
                    updatedLatLng: oldLocationEntity.geometry.latLng,
                    updatedConfidence: oldLocationEntity.geometry.confidence,
                    updatedProvider: oldLocationEntity.geometry.provider,
                    updatedIsOriginal: oldLocationEntity.isOriginal
                }
            );
            updatedLocations = updatedLocations.push(updatedLocation);
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedAllLocations, updatedLocations };
    }

    static resetMultipleLocationsGeometryFields({
        oldLocationEntities,
        allLocations
    }) {
        let updatedLocations = Immutable.List();
        oldLocationEntities.forEach(oldLocationEntity => {
            const { geometry, originalGeometry } = oldLocationEntity;
            if (
                !geometry ||
                (!geometry.latLng && originalGeometry.latLng) ||
                (geometry &&
                    geometry.latLng &&
                    originalGeometry.latLng &&
                    geometry.latLng.isDifferent(originalGeometry.latLng))
            ) {
                const updatedLocation = GeocodeEditUtils.resetLocationGeometryFields(
                    {
                        editedLocation: oldLocationEntity
                    }
                );
                updatedLocations = updatedLocations.push(updatedLocation);
            }
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedAllLocations, updatedLocations };
    }

    static resetLocationGeometryFields({ editedLocation }) {
        const { originalGeometry } = editedLocation;
        return GeocodeEditUtils.updateLocationGeometryFields({
            editedLocation,
            updatedLatLng: originalGeometry.latLng,
            updatedIsOriginal: true,
            updatedConfidence: originalGeometry.confidence,
            updatedProvider: originalGeometry.provider
        });
    }

    static updateLocationGeometryFields({
        editedLocation,
        updatedLatLng,
        updatedIsOriginal,
        updatedProvider,
        updatedOriginalLatLng,
        updatedConfidence = LocationConfidenceLevels.High
    }: {
        editedLocation: GeocodeEditLocation,
        updatedLatLng: { lat: number, lng: number, accuracy: number },
        updatedOriginalLatLng?: { lat: number, lng: number, accuracy: number },
        updatedIsOriginal: boolean,
        updatedProvider?: string,
        updatedConfidence?: string
    }) {
        let newEditedLocation = GeocodeEditUtils.updateLocationIsOriginal({
            editedLocation,
            updatedIsOriginal
        });
        newEditedLocation = GeocodeEditUtils.updateLocationLatLng({
            editedLocation: newEditedLocation,
            updatedLatLng
        });
        newEditedLocation = GeocodeEditUtils.updateLocationConfidence({
            editedLocation: newEditedLocation,
            updatedConfidence
        });
        newEditedLocation = GeocodeEditUtils.updateLocationProvider({
            editedLocation: newEditedLocation,
            updatedProvider
        });
        if (updatedOriginalLatLng) {
            newEditedLocation = GeocodeEditUtils.updateLocationOriginalLatLng({
                editedLocation: newEditedLocation,
                updatedOriginalLatLng
            });
        }
        return newEditedLocation;
    }

    static updateMultipleLocationEntities({ allLocations, editedLocations }) {
        let updatedAllLocations = allLocations;
        editedLocations.forEach(editedLocation => {
            updatedAllLocations = GeocodeEditUtils.updateLocationEntities({
                allLocations: updatedAllLocations,
                editedLocation
            });
        });
        return updatedAllLocations;
    }

    static updateLocationEntities({
        allLocations,
        editedLocation
    }: {
        allLocations: GeocodeEditListType,
        editedLocation: GeocodeEditLocation
    }) {
        return allLocations.set(editedLocation.index - 1, editedLocation);
    }

    static resetMultipleLocationsVerification({
        allLocations,
        oldLocationEntities
    }) {
        let updatedLocations = Immutable.List();
        oldLocationEntities.forEach(location => {
            const updatedOldLocation = GeocodeEditUtils.updateLocationVerification(
                {
                    editedLocation: allLocations.get(location.index - 1),
                    verified: location.verified
                }
            );
            updatedLocations = updatedLocations.push(updatedOldLocation);
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedAllLocations, updatedLocations };
    }

    static updateMultipleLocationsVerification({
        allLocations,
        editedLocations,
        verified
    }) {
        let updatedLocations = Immutable.List();
        editedLocations.forEach(location => {
            if (location.verified !== verified) {
                const updatedLocation = GeocodeEditUtils.updateLocationVerification(
                    {
                        editedLocation: location,
                        verified
                    }
                );
                updatedLocations = updatedLocations.push(updatedLocation);
            }
        });
        const updatedAllLocations = GeocodeEditUtils.updateMultipleLocationEntities(
            {
                allLocations,
                editedLocations: updatedLocations
            }
        );
        return { updatedAllLocations, updatedLocations };
    }

    static updateLocationVerification({
        editedLocation,
        verified,
        isVerifyChanged = true
    }: {
        editedLocation: GeocodeEditLocation,
        verified: boolean,
        isVerifyChanged?: boolean
    }) {
        return editedLocation
            .set('verified', verified)
            .set('isVerifyChanged', isVerifyChanged);
    }

    static getGroupedGeocodeLocationsList(
        allLocations
    ): Immutable.List<GroupedGeocodeEditLocation> {
        const groupedLocations = GeocodeEditUtils.groupLocations(allLocations)
            .map((locationGroup, groupId) => {
                return GroupedGeocodeEditLocation.fromJS({
                    locations: locationGroup.toJS(),
                    groupId
                });
            })
            .toList();
        return groupedLocations;
    }

    static groupLocations(
        geocodeEditLocations: GeocodeEditListType
    ): Immutable.KeyedIterable<?string, Immutable.List<GeocodeEditLocation>> {
        return geocodeEditLocations.groupBy(loc => loc.groupId);
    }

    static filterLocations({
        locations,
        searchText,
        appliedFilters,
        listFilters
    }) {
        var searchRegex = new RegExp(searchText, 'i');

        return (
            locations &&
            locations.filter(locationEntity => {
                const isFoundInSearch =
                    (locationEntity.code &&
                        locationEntity.code.search(searchRegex) !== -1) ||
                    locationEntity.name.search(searchRegex) !== -1 ||
                    (locationEntity.address &&
                        locationEntity.address
                            .getFullAddress()
                            .search(searchRegex) !== -1) ||
                    locationEntity.index === searchRegex;
                const isFoundInFilters = listFilters.reduce(
                    (currentValue, filter) => {
                        const { id } = filter;
                        const appliedFilter = appliedFilters[id];
                        const { value } = appliedFilter || {};
                        return (
                            currentValue &&
                            filter.filterEntity?.({
                                value,
                                filter,
                                entity: locationEntity
                            })
                        );
                    },
                    true
                );
                return isFoundInFilters && isFoundInSearch;
            })
        );
    }

    static getEditGeocodeLocationsList({
        geocodeEditLocations,
        groupBy,
        geocodeValidatedGeometries
    }: {
        geocodeEditLocations: Immutable.List<LocationWithMetadata>,
        groupBy?: ?string,
        geocodeValidatedGeometries: Immutable.Map<
            string,
            GeocodeVerifiedGeometry
        >
    }): GeocodeEditListType {
        let locationsList = Immutable.List();
        geocodeEditLocations &&
            geocodeEditLocations.forEach((locationWithMetadata, index) => {
                const { location, metadata } = locationWithMetadata;
                const {
                    id,
                    latLng,
                    geocodingMetadata,
                    address,
                    name,
                    code
                } = location;
                let verified = false;
                let confidence = LocationConfidenceLevels.High;
                let provider = '';
                let lat = 0;
                let lng = 0;
                let accuracy = 0;
                let groupId;
                if (groupBy && _.get(locationWithMetadata, groupBy)) {
                    groupId = _.get(locationWithMetadata, groupBy);
                } else {
                    groupId = 'No Group';
                }
                if (geocodingMetadata) {
                    ({ verified, confidence, provider } = geocodingMetadata);
                }
                if (latLng) {
                    ({ lat, lng, accuracy } = latLng);
                    if (!confidence) {
                        confidence = LocationConfidenceLevels.High;
                    }
                }

                if (!latLng) {
                    confidence = LocationConfidenceLevels.Low;
                }
                const geometry = {
                    latLng: latLng
                        ? {
                              lat,
                              lng,
                              accuracy
                          }
                        : null,
                    confidence,
                    provider
                };
                const geocodeVerifiedGeometry = geocodeValidatedGeometries?.get(
                    id
                );
                const groupedLocation = GeocodeEditLocation.fromJS({
                    id,
                    code,
                    groupId,
                    index: index + 1,
                    isOriginal: true,
                    geometry,
                    originalGeometry: geometry,
                    name,
                    verified,
                    metadata
                })
                    .setAddress(address)
                    .setGeocodeVerifiedGeometry(geocodeVerifiedGeometry);
                locationsList = locationsList.push(groupedLocation);
            });
        return locationsList;
    }
    @memoized()
    static getLatLngsFromLocations(locations) {
        if (!locations) return;
        return locations
            .filter(location => location.geometry.latLng)
            .map(location => {
                return location.geometry.latLng;
            })
            .toJS();
    }

    @memoized()
    static getAllLocationsBounds(locations) {
        const latLngs = GeocodeEditUtils.getLatLngsFromLocations(locations);
        return LatLngUtils.getBoundsFromLatLngs(latLngs);
    }

    static getClusterColorByConfidence({
        points,
        shouldShowOriginalColor,
        overrideClusterConfidenceColor
    }) {
        const overridenConfidenceColor = overrideClusterConfidenceColor(points);
        if (overridenConfidenceColor) {
            return overridenConfidenceColor;
        }
        const pointWithLowConfidence = _.find(points, point => {
            return (
                point.child.props.confidence === LocationConfidenceLevels.Low
            );
        });
        if (pointWithLowConfidence) {
            return getMarkerColorFromConfidence(
                LocationConfidenceLevels.Low,
                shouldShowOriginalColor
            );
        }

        const pointWithMediumConfidence = _.find(points, point => {
            return (
                point.child.props.confidence === LocationConfidenceLevels.Medium
            );
        });

        if (pointWithMediumConfidence) {
            return getMarkerColorFromConfidence(
                LocationConfidenceLevels.Medium,
                shouldShowOriginalColor
            );
        }

        return getMarkerColorFromConfidence(
            LocationConfidenceLevels.High,
            shouldShowOriginalColor
        );
    }
    static updateMultipleLocationVerifiedGeometries({
        allLocations,
        geocodeValidatedGeometries
    }: {
        allLocations: Immutable.List<GeocodeEditLocation>,
        geocodeValidatedGeometries: Immutable.Map<
            string,
            GeocodeVerifiedGeometry
        >
    }): Immutable.List<GeocodeEditLocation> {
        return allLocations.map(location => {
            const geocodeVerifiedGeometry = geocodeValidatedGeometries?.get(
                location.id
            );
            return location.setGeocodeVerifiedGeometry(geocodeVerifiedGeometry);
        });
    }
}

class GeocodeFilterUtils {
    static VerifiedStatusEnum = {
        verified: 'verified',
        unverified: 'unverified'
    };

    static VERIFIED_OPTIONS = Object.keys(
        GeocodeFilterUtils.VerifiedStatusEnum
    ).map(status => {
        return {
            value: status,
            label: nsTranslate(
                `entityManagement:masterLocationProperties.verificationStatus.${status}`
            )
        };
    });

    static GEOCODE_PROVIDER_OPTIONS = Object.keys(ProviderEnumType)
        .filter(provider => {
            if (provider === ProviderEnumType.CLIENT_READ) {
                return false;
            }
            return true;
        })
        .map(provider => {
            return {
                value: provider,
                label: nsTranslate(
                    `entityManagement:masterLocationProperties.geocodeProvider.${provider}`
                ),
                leftIcon:
                    provider === ProviderEnumType.LOCUS_GEOCODER
                        ? 'system'
                        : 'avatar'
            };
        });
    static GEOCODE_ACCURACY_OPTIONS = Object.keys(AccuracyCategoryType)
        .filter(accuracy => {
            if (accuracy === AccuracyCategoryType.HIGHLY_INACCURATE) {
                return false;
            }
            return true;
        })
        .map(accuracy => {
            return {
                value: accuracy,
                label: nsTranslate(`geocodeMap.accuracyCategory.${accuracy}`)
            };
        });

    static LOCATION_EDITED_FILTER = UIFilter.fromJS({
        id: 'LOCATION_EDITED',
        labelKey: 'geocodeMap.geocodeEdited',
        element: FilterElementsEnum.Checkbox,
        optionToIdMap: {
            edited: [],
            unedited: []
        },
        filterEntity: ({ value, entity }) => {
            return value ? !entity.isOriginal : true;
        }
    });

    static LOCATION_VERIFIED_FILTER = UIFilter.fromJS({
        id: 'LOCATION_VERIFIED',
        labelKey: 'geocodeMap.geocodeVerified',
        options: GeocodeFilterUtils.VERIFIED_OPTIONS,
        optionToIdMap: {
            verified: []
        },
        filterEntity: ({ value, entity }) => {
            const { verified } = entity;
            if (!value || !value.length) {
                return true;
            }
            let shouldKeep = true;
            if (
                value.includes(
                    GeocodeFilterUtils.VerifiedStatusEnum.verified
                ) &&
                !value.includes(
                    GeocodeFilterUtils.VerifiedStatusEnum.unverified
                )
            ) {
                shouldKeep = verified;
            }
            if (
                value.includes(
                    GeocodeFilterUtils.VerifiedStatusEnum.unverified
                ) &&
                !value.includes(GeocodeFilterUtils.VerifiedStatusEnum.verified)
            ) {
                shouldKeep = !verified;
            }
            return shouldKeep;
        }
    });

    static GEOCODE_PROVIDER_FILTER = UIFilter.fromJS({
        id: 'GEOCODE_PROVIDER',
        labelKey: 'geocodeMap.geocodeSource',
        options: GeocodeFilterUtils.GEOCODE_PROVIDER_OPTIONS,
        filterEntity: ({ value, entity }) => {
            const { provider } = entity.geometry || {};
            let newValue = value;
            if (!newValue) {
                return true;
            }
            if (value.includes(ProviderEnumType.CLIENT_GEOCODER)) {
                // #HACK, include CLIENT_READ when CLIENT_GEOCODER is selected
                newValue = [...newValue, ProviderEnumType.CLIENT_READ];
            }
            if (!provider || !newValue.length || newValue.includes(provider)) {
                return true;
            }
            return false;
        }
    });

    static GEOCODE_ACCURACY_FILTER = UIFilter.fromJS({
        id: 'GEOCODE_ACCURACY',
        labelKey: 'geocodeMap.checkGeocodeAccuracy',
        options: GeocodeFilterUtils.GEOCODE_ACCURACY_OPTIONS,
        filterEntity: ({ value, entity }) => {
            const { geocodeVerifiedGeometry } = entity;
            const { accuracyCategory } = geocodeVerifiedGeometry || {};
            let newValue = value;
            if (!newValue) {
                return true;
            }
            if (value.includes(AccuracyCategoryType.POSSIBLY_INACCURATE)) {
                // #HACK, include HIGHLY_INACCURATE when POSSIBLY_INACCURATE is selected
                newValue = [
                    ...newValue,
                    AccuracyCategoryType.HIGHLY_INACCURATE
                ];
            }
            if (
                !accuracyCategory ||
                !newValue.length ||
                newValue.includes(accuracyCategory)
            ) {
                return true;
            }
            return false;
        }
    });

    static DEFAULT_FILTERS = Immutable.List([
        GeocodeFilterUtils.LOCATION_EDITED_FILTER,
        GeocodeFilterUtils.LOCATION_VERIFIED_FILTER,
        GeocodeFilterUtils.GEOCODE_PROVIDER_FILTER
    ]);
    static ALL_FILTERS = Immutable.List([
        GeocodeFilterUtils.LOCATION_EDITED_FILTER,
        GeocodeFilterUtils.LOCATION_VERIFIED_FILTER,
        GeocodeFilterUtils.GEOCODE_PROVIDER_FILTER,
        GeocodeFilterUtils.GEOCODE_ACCURACY_FILTER
    ]);
}

export { GeocodeFilterUtils };

export default GeocodeEditUtils;
